// client/src/components/Common/YearSelector.js
import React, { useEffect, useMemo, useState } from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select, CircularProgress } from '@mui/material';
import { fetchJson } from '../../utils/fetchJson';
import {
  syncAcademicYearWithServer, getCurrentAcademicYearId, setCurrentAcademicYearId,
  fetchSchoolCurrentAcademicYear, onAcademicYearChanged,
} from '../../utils/academicYearContext';

export default function YearSelector({ size = 'small', fullWidth = false, label = 'Academic Year', onChange }) {
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [value, setValue] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // The list is every global year — the options to choose from.
        const list = await fetchJson('/api/global-academic-years', { method: 'GET' });
        const items = Array.isArray(list?.years) ? list.years : (Array.isArray(list) ? list : []);
        if (mounted) setYears(items);

        // But WHICH one is current is the school's decision, not the first row
        // of that list. Selecting `items.find(isCurrent) || items[0]` picked the
        // lowest id whenever no year carried the isCurrent flag — the oldest
        // session — and then wrote it to storage, so the switcher pinned the app
        // to a past year and every scoped request followed it.
        const school = await fetchSchoolCurrentAcademicYear();

        // SHOW WHAT THE APP IS ACTUALLY SCOPED TO — the phone's rule.
        //
        // The drawer chip on mobile displays the STORED year, so the control
        // and the data always agree about which session you are looking at.
        // This did the opposite: it resolved the school's current year and both
        // displayed AND persisted it on every mount, discarding whatever the
        // user had chosen. A switcher that overwrites your selection whenever it
        // mounts is not a switcher, and with the year bar now on every portal it
        // mounts far more often than it used to.
        //
        // Resetting a stale year is still necessary — a school rolls over and
        // nobody should be left in last session — but that belongs to
        // bootstrapAcademicYearFromServer, which runs at load and after sign-in,
        // exactly as bootstrapScope does on the phone. One owner for the reset,
        // one owner for the choice; before this they were both, and fought.
        //
        // `items[0]` is the lowest id, i.e. the OLDEST session, so it is never
        // persisted — fetchSchoolCurrentAcademicYear swallows every failure and
        // returns null, and one bad moment on a bad network would otherwise pin
        // the whole app to the school's first-ever year.
        const stored = getCurrentAcademicYearId();
        const authoritative = school?.id ?? items.find((y) => y?.isCurrent)?.id ?? null;

        if (mounted) {
          // A stored selection wins: it is what the requests are already
          // carrying. Otherwise the school's year, and only then a bare guess.
          const shown = stored ?? authoritative ?? items[0]?.id ?? null;
          if (shown) setValue(Number(shown));
          // Persist ONLY to fill a gap — never to overrule an existing choice.
          if (!stored && authoritative) {
            setCurrentAcademicYearId(school || authoritative);
          }
        }
      } catch (_) {
        // Non-fatal; component still renders and user can pick
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Follow the year when something else resolves it.
  //
  // The post-login bootstrap settles AFTER this component has mounted, so
  // without this the dropdown keeps displaying whatever it read on mount while
  // every request already carries the newly resolved year — the switcher and
  // the data would disagree about which session you are looking at.
  useEffect(() => onAcademicYearChanged((id) => {
    if (Number.isFinite(Number(id)) && Number(id) > 0) setValue(Number(id));
  }), []);

  const options = useMemo(() => {
    return years.map(y => ({
      id: y.id,
      label: y.name || y.label || `${y.startsAt ? new Date(y.startsAt).getFullYear() : ''}/${y.endsAt ? new Date(y.endsAt).getFullYear() : ''}`.replace(/^\/?|\/?$/g, ''),
    }));
  }, [years]);

  const handleChange = async (e) => {
    const id = Number(e.target.value);
    setValue(id);
    setSaving(true);
    try {
      // THE WHOLE ROW, not just the id — the phone stores name and dates
      // alongside it so nothing has to refetch the catalogue to name the year
      // or work out the session's month range.
      const picked = years.find((y) => Number(y?.id) === id) || id;
      // Persist locally and emit app:ay:changed for reactive pages
      setCurrentAcademicYearId(picked);
      // Best-effort server sync (non-blocking semantics retained)
      await syncAcademicYearWithServer(id);
      if (onChange) onChange(id);
    } catch (_) {
      // Surface errors at page level if needed; keep UX minimal here
    } finally {
      setSaving(false);
    }
  };

  const selectedLabel = options.find(o => o.id === value)?.label || '';

  return (
    <Box sx={{ minWidth: { xs: 0, sm: 200 }, width: fullWidth ? '100%' : { xs: '100%', sm: 'auto' }, maxWidth: '100%', display: 'flex', alignItems: 'center', flex: fullWidth ? 1 : { xs: 1, sm: '0 1 auto' } }}>
      <FormControl size={size} fullWidth={fullWidth} disabled={loading || saving}>
        {label ? <InputLabel>{label}</InputLabel> : null}
        <Select
          value={value || ''}
          onChange={handleChange}
          displayEmpty
          label={label || undefined}
          renderValue={(val) => {
            if (!val) return (loading ? 'Loading years…' : (label ? label : 'Select academic year'));
            return selectedLabel;
          }}
        >
          <MenuItem value="" disabled>
            {loading ? 'Loading years…' : (label ? label : 'Select academic year')}
          </MenuItem>
          {options.map(opt => (
            <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>
          ))}
        </Select>
      </FormControl>
      {(loading || saving) && <CircularProgress size={18} sx={{ ml: 1 }} />}
    </Box>
  );
}
